type BlockLayout = {
  x: number;
  y: number;
  w: number;
  h: number;
};

type BlockRecord = {
  id: string;
  type: string;
  layout: BlockLayout;
  content?: Record<string, unknown>;
  style?: Record<string, unknown>;
};

export type RequestFormBlockContent = {
  title: string;
  description: string;
  submitLabel: string;
  submittingLabel: string;
  emptyMessage: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  commentsLabel: string;
  commentsPlaceholder: string;
  newsletterLabel: string;
  footerText: string;
};

export type EnquiryFormTextContent = {
  heading: string;
  description: string;
  emptyCartMessage: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  commentsLabel: string;
  commentsPlaceholder: string;
  newsletterLabel: string;
  submitButtonLabel: string;
  submittingButtonLabel: string;
  footerText: string;
};

export interface EnquirySectionContent {
  sections?: {
    main?: Partial<EnquiryFormTextContent>;
  };
}

export const DEFAULT_ENQUIRY_CONTENT: EnquirySectionContent = {
  sections: {
    main: {
      heading: 'Place Your Order Request',
      description:
        "Fill out this form to enquire about your selected vases and colour combinations. We'll get back to you shortly with availability and pricing.",
      emptyCartMessage: 'Your cart is empty. Add vase and colour combinations above to create your enquiry.',
      nameLabel: 'Your Name *',
      namePlaceholder: 'Enter your name',
      emailLabel: 'Your Email Address *',
      emailPlaceholder: 'Enter your email address',
      commentsLabel: "Anything else you'd like to ask or tell us?",
      commentsPlaceholder: 'Enter any additional comments or questions',
      newsletterLabel:
        'I would also like to receive email updates, new releases, and occasional offers. You can unsubscribe at any time.',
      submitButtonLabel: 'Send Enquiry',
      submittingButtonLabel: 'Sending Enquiry...',
      footerText: 'Your enquiry will be sent to {emailTo}',
    },
  },
};

export const DEFAULT_REQUEST_FORM_BLOCK_CONTENT: RequestFormBlockContent = {
  title: 'Place Your Order Request',
  description:
    "Fill out this form to enquire about your selected designs. We'll get back to you shortly with availability and pricing.",
  submitLabel: 'Send Enquiry',
  submittingLabel: 'Sending Enquiry...',
  emptyMessage:
    'No request items yet. Connect this form to Request Items in the Logic Board, then add items from the page.',
  nameLabel: 'Your Name *',
  namePlaceholder: 'Enter your name',
  emailLabel: 'Your Email Address *',
  emailPlaceholder: 'Enter your email address',
  commentsLabel: "Anything else you'd like to ask or tell us?",
  commentsPlaceholder: 'Enter any additional comments or questions',
  newsletterLabel:
    'I would also like to receive email updates, new releases, and occasional offers. You can unsubscribe at any time.',
  footerText: 'Your enquiry will be sent to {emailTo}',
};

export const ENQUIRY_REQUEST_FORM_BLOCK_ID = 'enquiry-request-form-block';

export const ENQUIRY_REQUEST_FORM_DEFAULT_LAYOUT: BlockLayout = {
  x: 2,
  y: 2,
  w: 20,
  h: 12,
};

function resolveContentString(
  content: Record<string, unknown> | undefined,
  key: keyof RequestFormBlockContent
) {
  const value = content?.[key];
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  return DEFAULT_REQUEST_FORM_BLOCK_CONTENT[key];
}

function getBlockRole(block: BlockRecord) {
  const style =
    block.style && typeof block.style === 'object'
      ? (block.style as Record<string, unknown>)
      : undefined;
  return typeof style?.role === 'string' ? style.role : '';
}

function getLegacyEnquiryBlockContent(
  blocks: BlockRecord[],
  role: string
): Record<string, unknown> | undefined {
  const match = blocks.find((block) => getBlockRole(block) === role);
  return match?.content && typeof match.content === 'object'
    ? (match.content as Record<string, unknown>)
    : undefined;
}

function getLegacyEnquiryText(
  blocks: BlockRecord[],
  role: string,
  key: string,
  fallback: string
) {
  const content = getLegacyEnquiryBlockContent(blocks, role);
  const value = content?.[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;
}

function isLegacyEnquiryBlock(block: BlockRecord) {
  const style =
    block.style && typeof block.style === 'object'
      ? (block.style as Record<string, unknown>)
      : undefined;
  return style?.__template_slot === 'enquiry';
}

export function resolveRequestFormContent(
  content: Record<string, unknown> | undefined
): RequestFormBlockContent {
  return {
    title: resolveContentString(content, 'title'),
    description: resolveContentString(content, 'description'),
    submitLabel: resolveContentString(content, 'submitLabel'),
    submittingLabel: resolveContentString(content, 'submittingLabel'),
    emptyMessage: resolveContentString(content, 'emptyMessage'),
    nameLabel: resolveContentString(content, 'nameLabel'),
    namePlaceholder: resolveContentString(content, 'namePlaceholder'),
    emailLabel: resolveContentString(content, 'emailLabel'),
    emailPlaceholder: resolveContentString(content, 'emailPlaceholder'),
    commentsLabel: resolveContentString(content, 'commentsLabel'),
    commentsPlaceholder: resolveContentString(content, 'commentsPlaceholder'),
    newsletterLabel: resolveContentString(content, 'newsletterLabel'),
    footerText: resolveContentString(content, 'footerText'),
  };
}

export function normalizeEnquiryDraft(value: unknown): EnquirySectionContent {
  const raw = (value && typeof value === 'object' ? value : {}) as EnquirySectionContent;
  const main = raw.sections?.main ?? {};
  const defaults = DEFAULT_ENQUIRY_CONTENT.sections!.main!;
  const text = (val: unknown, fallback: string) =>
    typeof val === 'string' && val.trim() ? val : fallback;
  return {
    sections: {
      main: {
        heading: text(main.heading, defaults.heading!),
        description: text(main.description, defaults.description!),
        emptyCartMessage: text(main.emptyCartMessage, defaults.emptyCartMessage!),
        nameLabel: text(main.nameLabel, defaults.nameLabel!),
        namePlaceholder: text(main.namePlaceholder, defaults.namePlaceholder!),
        emailLabel: text(main.emailLabel, defaults.emailLabel!),
        emailPlaceholder: text(main.emailPlaceholder, defaults.emailPlaceholder!),
        commentsLabel: text(main.commentsLabel, defaults.commentsLabel!),
        commentsPlaceholder: text(main.commentsPlaceholder, defaults.commentsPlaceholder!),
        newsletterLabel: text(main.newsletterLabel, defaults.newsletterLabel!),
        submitButtonLabel: text(main.submitButtonLabel, defaults.submitButtonLabel!),
        submittingButtonLabel: text(main.submittingButtonLabel, defaults.submittingButtonLabel!),
        footerText: text(main.footerText, defaults.footerText!),
      },
    },
  };
}

export function extractLegacyEnquiryRequestFormContent(
  blocks: BlockRecord[]
): RequestFormBlockContent {
  return {
    title: getLegacyEnquiryText(
      blocks,
      'heading',
      'text',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.title
    ),
    description: getLegacyEnquiryText(
      blocks,
      'description',
      'text',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.description
    ),
    submitLabel: getLegacyEnquiryText(
      blocks,
      'submit',
      'label',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.submitLabel
    ),
    submittingLabel: DEFAULT_REQUEST_FORM_BLOCK_CONTENT.submittingLabel,
    emptyMessage: getLegacyEnquiryText(
      blocks,
      'empty-cart',
      'text',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.emptyMessage
    ),
    nameLabel: getLegacyEnquiryText(
      blocks,
      'name-field',
      'label',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.nameLabel
    ),
    namePlaceholder: getLegacyEnquiryText(
      blocks,
      'name-field',
      'text',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.namePlaceholder
    ),
    emailLabel: getLegacyEnquiryText(
      blocks,
      'email-field',
      'label',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.emailLabel
    ),
    emailPlaceholder: getLegacyEnquiryText(
      blocks,
      'email-field',
      'text',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.emailPlaceholder
    ),
    commentsLabel: getLegacyEnquiryText(
      blocks,
      'comments-field',
      'label',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.commentsLabel
    ),
    commentsPlaceholder: getLegacyEnquiryText(
      blocks,
      'comments-field',
      'text',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.commentsPlaceholder
    ),
    newsletterLabel: getLegacyEnquiryText(
      blocks,
      'newsletter',
      'text',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.newsletterLabel
    ),
    footerText: getLegacyEnquiryText(
      blocks,
      'footer',
      'text',
      DEFAULT_REQUEST_FORM_BLOCK_CONTENT.footerText
    ),
  };
}

export function createEnquiryRequestFormBlock(
  content?: Partial<RequestFormBlockContent>,
  layout?: Partial<BlockLayout>
): BlockRecord {
  return {
    id: ENQUIRY_REQUEST_FORM_BLOCK_ID,
    type: 'request-form',
    layout: {
      ...ENQUIRY_REQUEST_FORM_DEFAULT_LAYOUT,
      ...(layout ?? {}),
    },
    content: {
      ...DEFAULT_REQUEST_FORM_BLOCK_CONTENT,
      ...(content ?? {}),
    },
  };
}

export function convertLegacyEnquiryBlocks(blocks: BlockRecord[]): BlockRecord[] {
  const hasRequestForm = blocks.some((block) => block.type === 'request-form');
  if (hasRequestForm) return blocks;

  const legacyBlocks = blocks.filter(isLegacyEnquiryBlock);
  if (legacyBlocks.length === 0) return blocks;

  const minX = Math.min(...legacyBlocks.map((block) => block.layout.x));
  const minY = Math.min(...legacyBlocks.map((block) => block.layout.y));
  const maxX = Math.max(
    ...legacyBlocks.map((block) => block.layout.x + block.layout.w - 1)
  );
  const maxY = Math.max(
    ...legacyBlocks.map((block) => block.layout.y + block.layout.h - 1)
  );

  const nextLayout: BlockLayout = {
    x: minX,
    y: minY,
    w: maxX - minX + 1,
    h: maxY - minY + 1,
  };

  const preservedBlocks = blocks.filter((block) => !isLegacyEnquiryBlock(block));
  return [
    createEnquiryRequestFormBlock(
      extractLegacyEnquiryRequestFormContent(legacyBlocks),
      nextLayout
    ),
    ...preservedBlocks,
  ];
}
